import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { UploadSimple, GraduationCap, FloppyDisk } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import api, { formatApiError, API } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function Settings() {
  const { settings, refreshSettings } = useAuth();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  useEffect(() => { if (settings) setForm(settings); }, [settings]);

  const save = async () => {
    setSaving(true);
    try { await api.put("/settings", form); await refreshSettings(); toast.success("Pengaturan tersimpan"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "logo");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const updated = { ...form, logo_path: data.path };
      setForm(updated);
      await api.put("/settings", updated);
      await refreshSettings();
      toast.success("Logo diperbarui");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal unggah"); }
  };

  const logoUrl = form.logo_path ? `${API}/files/${form.logo_path}` : null;

  return (
    <div data-testid="settings-page" className="max-w-3xl">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-[#0B8D95] font-semibold mb-1">Sistem</div>
        <h1 className="font-display text-3xl font-black text-gray-900">Pengaturan Yayasan</h1>
        <p className="text-gray-600 text-sm mt-1">Kelola identitas & informasi yayasan.</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-md p-5 space-y-4">
        {/* Logo row - compact */}
        <div className="flex items-center gap-4 pb-4 border-b border-gray-100">
          <div className="h-16 w-16 rounded-md border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden shrink-0">
            {logoUrl ? <img src={logoUrl} alt="Logo" className="max-h-full max-w-full object-contain" /> : <GraduationCap size={32} className="text-gray-300" weight="duotone" />}
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-gray-900">Logo Yayasan</div>
            <div className="text-xs text-gray-500">PNG/JPG, disarankan 512×512</div>
          </div>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e)=>e.target.files?.[0] && uploadLogo(e.target.files[0])} data-testid="logo-file-input" />
          <Button variant="outline" size="sm" onClick={()=>fileRef.current?.click()} data-testid="upload-logo-button">
            <UploadSimple size={14} className="mr-1.5" />Unggah
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2"><Label>Nama Yayasan *</Label><Input value={form.foundation_name || ""} onChange={(e)=>setForm({...form, foundation_name: e.target.value})} data-testid="foundation-name-input" /></div>
          <div><Label>Nama Singkat</Label><Input value={form.foundation_short_name || ""} onChange={(e)=>setForm({...form, foundation_short_name: e.target.value})} /></div>
          <div><Label>Website</Label><Input value={form.website || ""} onChange={(e)=>setForm({...form, website: e.target.value})} /></div>
          <div className="md:col-span-2"><Label>Tagline / Motto</Label><Input value={form.tagline || ""} onChange={(e)=>setForm({...form, tagline: e.target.value})} /></div>
          <div className="md:col-span-2"><Label>Alamat</Label><Textarea rows={2} value={form.address || ""} onChange={(e)=>setForm({...form, address: e.target.value})} /></div>
          <div><Label>Kota</Label><Input value={form.city || ""} onChange={(e)=>setForm({...form, city: e.target.value})} placeholder="Bandung" data-testid="city-input" /></div>
          <div><Label>Telepon</Label><Input value={form.phone || ""} onChange={(e)=>setForm({...form, phone: e.target.value})} /></div>
          <div className="md:col-span-2"><Label>Email</Label><Input type="email" value={form.email || ""} onChange={(e)=>setForm({...form, email: e.target.value})} /></div>
        </div>

        <div className="pt-4 border-t border-gray-100">
          <div className="text-sm font-bold text-gray-900 mb-1">Pejabat Penandatangan Dokumen</div>
          <div className="text-xs text-gray-500 mb-3">Nama-nama ini akan tampil sebagai default di kwitansi, slip gaji, dan laporan. Bisa di-override saat cetak per dokumen.</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label>Bendahara Sekolah</Label><Input value={form.signatory_bendahara || ""} onChange={(e)=>setForm({...form, signatory_bendahara: e.target.value})} placeholder="Nama bendahara" data-testid="sig-bendahara-input" /></div>
            <div><Label>Manager Keuangan</Label><Input value={form.signatory_manager_keuangan || ""} onChange={(e)=>setForm({...form, signatory_manager_keuangan: e.target.value})} placeholder="Nama manager keuangan" data-testid="sig-manager-input" /></div>
            <div><Label>Direktur</Label><Input value={form.signatory_direktur || ""} onChange={(e)=>setForm({...form, signatory_direktur: e.target.value})} placeholder="Nama direktur" data-testid="sig-direktur-input" /></div>
            <div><Label>Ketua Yayasan</Label><Input value={form.signatory_ketua_yayasan || ""} onChange={(e)=>setForm({...form, signatory_ketua_yayasan: e.target.value})} placeholder="Nama ketua yayasan" data-testid="sig-ketua-input" /></div>
          </div>
        </div>

        <Button onClick={save} disabled={saving} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="settings-save-button">
          <FloppyDisk size={16} className="mr-1.5" />{saving ? "Menyimpan..." : "Simpan Pengaturan"}
        </Button>
      </div>
    </div>
  );
}
